<template>
  <main class="creative-board-page">
    <div class="creative-shell">
      <MarketingToolHeader
        eyebrow="Kreativwerkstatt"
        :title="categoryTitle"
        description="Teile deine Erfahrungen, entdecke Best Practices und entwickle das Thema gemeinsam mit anderen weiter."
        icon="mdi-lightbulb-group-outline">
        <template v-slot:actions>
          <v-btn
            icon
            class="icon-action academy-local-back"
            aria-label="Zurück zur Kreativwerkstatt"
            title="Zurück zur Kreativwerkstatt"
            @click="$router.push('/akademie/kreativwerkstatt')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn color="#009640" depressed class="primary-action" @click="showCreateDialog = true">
            <v-icon left>mdi-plus</v-icon>
            Idee teilen
          </v-btn>
        </template>
      </MarketingToolHeader>

      <section class="board-toolbar" aria-label="Beitragsübersicht">
        <div>
          <span class="toolbar-icon"><v-icon>mdi-message-text-outline</v-icon></span>
          <div>
            <strong>{{ ideas.length }}</strong
            ><span>{{ ideas.length === 1 ? "Beitrag" : "Beiträge" }}</span>
          </div>
        </div>
        <p>Praxiswissen aus der Community – kompakt an einem Ort.</p>
      </section>

      <div v-if="loading" class="loading-grid">
        <v-skeleton-loader v-for="index in 4" :key="index" dark type="image, article" class="idea-skeleton" />
      </div>

      <div v-else-if="ideas.length" class="masonry-grid">
        <idea-card v-for="idea in ideas" :key="idea._id" :idea="idea" @click="openIdea(idea)" @like="toggleLike" />
      </div>

      <div v-else class="empty-surface">
        <span><v-icon>mdi-message-draw</v-icon></span>
        <h2>Noch keine Ideen in dieser Kategorie</h2>
        <p>Sei die erste Person, die eine Best Practice mit der Community teilt.</p>
        <v-btn color="#009640" depressed class="primary-action" @click="showCreateDialog = true">
          <v-icon left>mdi-plus</v-icon>Ersten Beitrag teilen
        </v-btn>
      </div>

      <v-dialog
        v-model="detailDialog"
        max-width="48.75rem"
        dark
        scrollable
        :fullscreen="$vuetify.breakpoint.xsOnly"
        content-class="academy-idea-dialog">
        <v-card v-if="activeIdea" dark class="dialog-card">
          <div class="dialog-header">
            <v-avatar size="40" color="#252826"
              ><span>{{ initials(activeIdea.authorName) }}</span></v-avatar
            >
            <div>
              <strong>{{ activeIdea.authorName }}</strong>
              <small>{{ formatDateTime(activeIdea.createdAt) }}</small>
            </div>
            <v-btn icon class="dialog-icon-action" aria-label="Beitrag schliessen" @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-card-text class="dialog-body">
            <h2>{{ activeIdea.title }}</h2>
            <p class="idea-description">{{ activeIdea.description }}</p>

            <div v-if="activeIdea.attachments && activeIdea.attachments.length" class="attachments-grid">
              <div v-for="attachment in activeIdea.attachments" :key="attachment.fileId" class="attachment-item">
                <SecuredMedia
                  :src="attachmentPath(activeIdea._id, attachment.fileId)"
                  :media-type="attachmentType(attachment)"
                  :stream-playback="attachmentType(attachment) === 'video'"
                  :download-name="attachment.originalName"
                  eager />
              </div>
            </div>

            <div class="engagement-row">
              <v-btn text class="engagement-action" :color="activeIdea.likedByMe ? '#ff6b78' : '#b8b8bc'" @click="toggleLike(activeIdea._id)">
                <v-icon left>{{ activeIdea.likedByMe ? "mdi-heart" : "mdi-heart-outline" }}</v-icon>
                {{ activeIdea.likeCount || 0 }} Likes
              </v-btn>
              <span><v-icon>mdi-comment-outline</v-icon>{{ activeIdea.comments ? activeIdea.comments.length : 0 }} Kommentare</span>
            </div>

            <section class="comments-section">
              <div class="comments-heading">
                <div class="surface-eyebrow">Austausch</div>
                <h3>Kommentare</h3>
              </div>

              <div v-if="activeIdea.comments && activeIdea.comments.length" class="comments-list">
                <article v-for="comment in activeIdea.comments" :key="comment._id" class="comment-item">
                  <v-avatar size="32" color="#252826"
                    ><span>{{ initials(comment.authorName) }}</span></v-avatar
                  >
                  <div>
                    <div class="comment-meta">
                      <strong>{{ comment.authorName }}</strong
                      ><time>{{ formatDateTime(comment.createdAt) }}</time>
                    </div>
                    <p>{{ comment.text }}</p>
                  </div>
                  <v-btn v-if="canDeleteComment(comment)" icon class="comment-delete" aria-label="Kommentar löschen" @click="deleteComment(comment)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </article>
              </div>
              <div v-else class="comments-empty">Noch keine Kommentare. Starte den Austausch.</div>

              <div class="comment-composer">
                <v-textarea
                  v-model="newComment"
                  placeholder="Schreib einen Kommentar..."
                  outlined
                  rows="2"
                  auto-grow
                  hide-details
                  color="#58d388"
                  class="modern-field" />
                <v-btn
                  color="#009640"
                  depressed
                  class="send-action"
                  :disabled="!newComment.trim()"
                  :loading="postingComment"
                  aria-label="Kommentar senden"
                  @click="postComment">
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
            </section>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showCreateDialog"
        max-width="37.5rem"
        dark
        persistent
        scrollable
        :fullscreen="$vuetify.breakpoint.xsOnly"
        content-class="academy-create-idea-dialog">
        <v-card dark class="dialog-card create-card">
          <div class="create-header">
            <span><v-icon>mdi-lightbulb-on-outline</v-icon></span>
            <div>
              <div class="surface-eyebrow">Kreativwerkstatt</div>
              <h2>Neue Idee teilen</h2>
              <p>Teile eine konkrete Erfahrung oder Best Practice.</p>
            </div>
            <v-btn icon class="dialog-icon-action" aria-label="Dialog schliessen" :disabled="submitting" @click="showCreateDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>

          <v-card-text class="create-body">
            <v-text-field v-model="newIdea.title" label="Titel" outlined color="#58d388" class="modern-field" hide-details="auto" />
            <v-textarea
              v-model="newIdea.description"
              label="Beschreibung oder Praxis-Beispiel"
              outlined
              color="#58d388"
              rows="5"
              class="modern-field"
              hide-details="auto" />
            <v-file-input
              v-model="newIdea.files"
              label="Anhänge"
              outlined
              multiple
              chips
              :disabled="submitting"
              color="#58d388"
              prepend-icon="mdi-paperclip"
              class="modern-field"
              hint="Optional: Bilder, Videos oder Dokumente"
              persistent-hint />
            <v-progress-linear v-if="submitting && uploadProgress > 0" :value="uploadProgress" color="#58d388" height="8" rounded class="mt-3" />
          </v-card-text>

          <v-card-actions class="create-actions">
            <v-btn text color="#d4d4d8" class="secondary-action" :disabled="submitting" @click="showCreateDialog = false">Abbrechen</v-btn>
            <v-spacer />
            <v-btn
              color="#009640"
              depressed
              class="primary-action"
              :loading="submitting"
              :disabled="!newIdea.title || !newIdea.description || submitting"
              @click="submitIdea">
              <v-icon left>mdi-send-outline</v-icon>
              Teilen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>{{ snackbar.text }}</v-snackbar>
    </div>
  </main>
</template>

<script>
import IdeaCard from "./IdeaCard.vue"
import SecuredMedia from "./SecuredMedia.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"
import api from "@/api"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

export default {
  name: "KreativBoard",
  components: { IdeaCard, SecuredMedia, MarketingToolHeader },
  data() {
    return {
      categoryTitle: "Kategorie wird geladen...",
      ideas: [],
      showCreateDialog: false,
      detailDialog: false,
      activeIdea: null,
      newComment: "",
      postingComment: false,
      newIdea: { title: "", description: "", files: [] },
      submitting: false,
      uploadProgress: 0,
      loading: false,
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}")
      } catch (error) {
        return {}
      }
    },
  },
  mounted() {
    this.loadIdeas()
    this.loadModuleInfo()
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    initials(name) {
      if (!name) return "?"
      const parts = name.trim().split(/\s+/)
      return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase()
    },
    formatDateTime(date) {
      if (!date) return ""
      const parsedDate = new Date(date)
      return parsedDate.toLocaleDateString("de-CH") + " " + parsedDate.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    async loadModuleInfo() {
      try {
        const moduleItem = await api.akademie.fetchModule(this.$route.params.id)
        if (moduleItem && moduleItem.title) this.categoryTitle = moduleItem.title
      } catch (error) {
        console.error("Kategorie konnte nicht geladen werden", error)
      }
    },
    async loadIdeas() {
      this.loading = true
      try {
        this.ideas = (await api.akademie.fetchIdeas(this.$route.params.id)) || []
      } catch (error) {
        console.error("Fehler beim Laden der Ideen:", error)
      } finally {
        this.loading = false
      }
    },
    async openIdea(idea) {
      this.detailDialog = true
      this.activeIdea = idea
      try {
        this.activeIdea = await api.akademie.fetchIdea(idea._id)
      } catch (error) {
        console.error(error)
      }
    },
    attachmentPath(ideaId, fileId) {
      const attachment = this.activeIdea && (this.activeIdea.attachments || []).find((item) => item.fileId === fileId)
      return attachment && attachment.mimeType && attachment.mimeType.startsWith("video/")
        ? api.akademie.getIdeaAttachmentPlaybackPath(ideaId, fileId)
        : api.akademie.getIdeaAttachmentPath(ideaId, fileId)
    },
    attachmentType(attachment) {
      if (!attachment || !attachment.mimeType) return "file"
      if (attachment.mimeType.startsWith("image/")) return "image"
      if (attachment.mimeType.startsWith("video/")) return "video"
      if (attachment.mimeType.startsWith("audio/")) return "audio"
      if (attachment.mimeType === "application/pdf") return "pdf"
      return "file"
    },
    async toggleLike(ideaId) {
      try {
        const result = await api.akademie.toggleLike(ideaId)
        const listIdea = this.ideas.find((idea) => idea._id === ideaId)
        if (listIdea) {
          this.$set(listIdea, "likedByMe", result.likedByMe)
          this.$set(listIdea, "likeCount", result.likeCount)
        }
        if (this.activeIdea && this.activeIdea._id === ideaId) {
          this.$set(this.activeIdea, "likedByMe", result.likedByMe)
          this.$set(this.activeIdea, "likeCount", result.likeCount)
        }
      } catch (error) {
        this.notify("error", "Liken fehlgeschlagen")
      }
    },
    async postComment() {
      if (!this.newComment.trim() || !this.activeIdea) return
      this.postingComment = true
      try {
        const comment = await api.akademie.addComment(this.activeIdea._id, this.newComment.trim())
        if (!this.activeIdea.comments) this.$set(this.activeIdea, "comments", [])
        this.activeIdea.comments.push(comment)
        const listIdea = this.ideas.find((idea) => idea._id === this.activeIdea._id)
        if (listIdea) this.$set(listIdea, "commentCount", (listIdea.commentCount || 0) + 1)
        this.newComment = ""
      } catch (error) {
        this.notify("error", "Kommentar konnte nicht gesendet werden")
      } finally {
        this.postingComment = false
      }
    },
    canDeleteComment(comment) {
      return this.currentUser && comment.authorId && comment.authorId.toString() === (this.currentUser._id || "").toString()
    },
    async deleteComment(comment) {
      if (!confirm("Kommentar löschen?")) return
      try {
        await api.akademie.deleteComment(this.activeIdea._id, comment._id)
        this.activeIdea.comments = this.activeIdea.comments.filter((entry) => entry._id !== comment._id)
        const listIdea = this.ideas.find((idea) => idea._id === this.activeIdea._id)
        if (listIdea && listIdea.commentCount > 0) listIdea.commentCount -= 1
      } catch (error) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },
    async submitIdea() {
      if (!this.newIdea.title || !this.newIdea.description) return
      this.submitting = true
      this.uploadProgress = 0
      const sessions = []
      let created = false
      try {
        const files = Array.isArray(this.newIdea.files) ? this.newIdea.files : this.newIdea.files ? [this.newIdea.files] : []
        if (files.length > 5) throw new Error("Maximal fünf Anhänge sind erlaubt.")
        if (files.some((file) => file.size > 250 * 1024 * 1024)) throw new Error("Ein Anhang ist grösser als 250 MB.")

        for (let index = 0; index < files.length; index += 1) {
          const file = files[index]
          const session = await api.akademie.prepareIdeaMediaUpload(this.$route.params.id, file)
          sessions.push(session)
          await uploadCloudflareMedia(file, session, {
            onProgress: (loaded, total) => {
              const fileProgress = loaded / Math.max(1, total || file.size)
              this.uploadProgress = ((index + fileProgress) / Math.max(1, files.length)) * 100
            },
          })
        }
        await api.akademie.createIdea({
          title: this.newIdea.title,
          description: this.newIdea.description,
          module: this.$route.params.id,
          attachments: sessions,
        })
        created = true
        this.showCreateDialog = false
        this.newIdea = { title: "", description: "", files: [] }
        await this.loadIdeas()
        this.notify("success", "Idee geteilt")
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || error.message || "Idee konnte nicht erstellt werden"
        this.notify("error", message)
      } finally {
        if (!created && sessions.length) {
          await Promise.all(sessions.map((session) => api.akademie.cancelIdeaMediaUpload(this.$route.params.id, session).catch(() => null)))
        }
        this.submitting = false
        this.uploadProgress = 0
      }
    },
  },
}
</script>

<style scoped>
.creative-board-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.creative-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.icon-action,
.dialog-icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.board-toolbar {
  display: flex;
  min-height: 5rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
  padding: 0.875rem 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: var(--surface);
}

.board-toolbar > div {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toolbar-icon {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.toolbar-icon .v-icon {
  color: currentColor;
}

.board-toolbar > div > div {
  display: grid;
}

.board-toolbar strong {
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.1;
}

.board-toolbar span,
.board-toolbar p {
  color: var(--muted);
  font-size: 0.875rem;
}

.board-toolbar p {
  margin: 0;
}

.masonry-grid {
  column-count: 4;
  column-gap: 1rem;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.idea-skeleton {
  min-height: 18rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  overflow: hidden;
  background: var(--surface) !important;
}

.empty-surface {
  display: flex;
  min-height: 24rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: var(--surface);
  text-align: center;
}

.empty-surface > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.empty-surface > span .v-icon {
  color: currentColor;
}

.empty-surface h2 {
  margin: 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.empty-surface p {
  max-width: 32rem;
  margin: 0.375rem 0 1rem;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.dialog-card {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: #1c1c1d !important;
}

.dialog-header,
.create-header,
.engagement-row,
.comment-meta,
.comment-composer {
  display: flex;
  align-items: center;
}

.dialog-header,
.create-header {
  flex: 0 0 auto;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
}

.dialog-header > div,
.create-header > div {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
}

.dialog-header strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.dialog-header small {
  color: var(--muted);
  font-size: 0.75rem;
}

.dialog-header .v-avatar span,
.comment-item .v-avatar span {
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 750;
}

.dialog-body,
.create-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.dialog-body > h2 {
  margin: 0;
  color: #ffffff;
  font-size: 1.5rem;
  line-height: 1.35;
}

.idea-description {
  margin: 0.625rem 0 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-wrap;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.attachment-item {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.engagement-row {
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.5rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.engagement-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  text-transform: none;
}

.engagement-row > span {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.engagement-row > span .v-icon {
  color: #8f9095;
  font-size: 1.125rem;
}

.comments-section {
  margin-top: 1.25rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.comments-heading h3,
.create-header h2 {
  margin: 0.1875rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.comments-list {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.875rem;
}

.comment-item {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) 2.75rem;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.028);
}

.comment-meta {
  flex-wrap: wrap;
  gap: 0.25rem 0.625rem;
}

.comment-meta strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.comment-meta time {
  color: var(--muted);
  font-size: 0.75rem;
}

.comment-item p {
  margin: 0.25rem 0 0;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.comment-delete {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  color: #a3a3a8 !important;
}

.comments-empty {
  margin-top: 0.875rem;
  padding: 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
  text-align: center;
}

.comment-composer {
  align-items: flex-end;
  gap: 0.5rem;
  margin-top: 0.875rem;
}

.modern-field {
  margin: 0;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.modern-field ::v-deep .v-messages {
  font-size: 0.875rem;
}

.send-action {
  width: 3rem !important;
  min-width: 3rem !important;
  height: 3rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
}

.create-header {
  align-items: flex-start;
}

.create-header > span {
  display: grid;
  width: 2.875rem;
  height: 2.875rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.create-header > span .v-icon {
  color: currentColor;
}

.create-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.create-body {
  display: grid;
  gap: 1rem;
}

.create-actions {
  flex: 0 0 auto;
  padding: 0.875rem 1.25rem max(0.875rem, env(safe-area-inset-bottom)) !important;
  border-top: 1px solid var(--border);
}

@media (max-width: 78.9375rem) {
  .creative-board-page {
    padding: 1.125rem;
  }

  .masonry-grid {
    column-count: 3;
  }

  .loading-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .masonry-grid {
    column-count: 2;
  }

  .loading-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .creative-board-page {
    padding: 0.75rem;
  }

  .board-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .masonry-grid {
    column-count: 1;
  }

  .loading-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .dialog-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header,
  .create-header,
  .dialog-body,
  .create-body,
  .create-actions {
    padding-right: 0.875rem !important;
    padding-left: 0.875rem !important;
  }

  .attachments-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .engagement-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .comment-composer {
    align-items: stretch;
    flex-direction: column;
  }

  .send-action {
    width: 100% !important;
  }

  .create-actions {
    flex-wrap: wrap;
  }

  .create-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
